import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Sheet, TextField } from "@components/ui";
import { MicIcon, MicSlashIcon, SpeakerIcon, SpeakerSlashIcon, PhoneHangupIcon, PhoneTransferIcon, PhoneIcon, PlusIcon } from "@icons";
import type { UseTwilioVoice } from "@hooks/useTwilioVoice";
import styles from "./CallOverlay.module.css";

export interface CallOverlayProps {
  voice: UseTwilioVoice;
}

function initialsOf(s: string): string {
  const letters = s.replace(/[^A-Za-z]/g, "");
  if (letters.length >= 2) return letters.slice(0, 2).toUpperCase();
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 2) return digits.slice(-2);
  return (s.trim()[0] ?? "?").toUpperCase();
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export function CallOverlay({ voice }: CallOverlayProps) {
  const { status, activeCall, incoming, hangup, mute, unmute, isMuted, answer, reject } = voice;
  const [seconds, setSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [speaker, setSpeaker] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTo, setMergeTo] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  // True once a warm transfer has pulled this call into a conference — later
  // merges add participants instead of creating a new conference.
  const [merged, setMerged] = useState(false);

  useEffect(() => {
    if (status !== "in-call" && status !== "connecting") {
      setSeconds(0);
      setMinimized(false);
      setMerged(false);
      return;
    }
    const start = Date.now();
    const id = window.setInterval(() => setSeconds(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [status, activeCall]);

  if (status === "idle" || status === "ready" || status === "offline" || status === "initializing" || status === "error") {
    return null;
  }

  const party = incoming?.from ?? (activeCall?.parameters?.From as string | undefined) ?? (activeCall?.parameters?.To as string | undefined) ?? "Unknown";
  const isIncoming = status === "incoming";

  // Announce the call to screen readers. Incoming calls are assertive so they
  // interrupt; connecting/active are polite. Kept as a stable per-state string
  // (not the ticking timer) so it announces once per transition, not per second.
  const liveMessage = isIncoming
    ? `Incoming call from ${party}`
    : status === "connecting"
    ? `Connecting call to ${party}`
    : status === "in-call"
    ? `On call with ${party}`
    : "";

  const toggleMute = () => {
    if (muted) {
      unmute();
      setMuted(false);
    } else {
      mute();
      setMuted(true);
    }
  };

  const onHangup = () => {
    setMinimized(false);
    hangup();
  };

  const closeTransfer = () => {
    setTransferOpen(false);
    setTransferError(null);
  };

  const submitTransfer = async () => {
    const to = transferTo.trim();
    if (!to || transferBusy) return;
    setTransferBusy(true);
    setTransferError(null);
    try {
      await voice.transfer(to);
      setTransferTo("");
      setTransferOpen(false);
    } catch (e) {
      setTransferError(e instanceof Error ? e.message : "Transfer failed. Please try again.");
    } finally {
      setTransferBusy(false);
    }
  };

  const submitMerge = async () => {
    const to = mergeTo.trim();
    if (!to || mergeBusy) return;
    setMergeBusy(true);
    setMergeError(null);
    try {
      if (merged) await voice.conference(to);
      else await voice.transfer(to, true);
      setMerged(true);
      setMergeTo("");
      setMergeOpen(false);
    } catch (e) {
      setMergeError(e instanceof Error ? e.message : "Couldn't add the participant. Please try again.");
    } finally {
      setMergeBusy(false);
    }
  };

  const overlay = (
    <>
      <div
        className={styles.srOnly}
        role={isIncoming ? "alert" : "status"}
        aria-live={isIncoming ? "assertive" : "polite"}
        aria-atomic="true"
      >
        {liveMessage}
      </div>
      {!minimized && (
        <div
          className={styles.root}
          role="dialog"
          aria-modal="true"
          aria-label={isIncoming ? `Incoming call from ${party}` : `Call with ${party}`}
        >
          <button className={styles.minimize} onClick={() => setMinimized(true)} aria-label="Minimize call">
            –
          </button>
          <div className={styles.card}>
            <div className={styles.avatarWrap} aria-hidden>
              {(isIncoming || status === "connecting") && <span className={styles.avatarRing} />}
              <div className={styles.avatar}>{initialsOf(party)}</div>
            </div>
            <div className={styles.party}>{party}</div>
            <div className={styles.status}>
              {isIncoming ? "Incoming call…" : status === "connecting" ? "Connecting…" : formatDuration(seconds)}
            </div>
            {!isIncoming && (
              <div className={styles.controls}>
                <button className={styles.controlBtn} onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
                  {muted ? <MicSlashIcon width={22} height={22} /> : <MicIcon width={22} height={22} />}
                </button>
                <button
                  className={styles.controlBtn}
                  onClick={() => setSpeaker((s) => !s)}
                  aria-label={speaker ? "Disable speaker" : "Enable speaker"}
                >
                  {speaker ? <SpeakerIcon width={22} height={22} /> : <SpeakerSlashIcon width={22} height={22} />}
                </button>
                <button
                  className={styles.controlBtn}
                  onClick={() => setMergeOpen(true)}
                  aria-label={merged ? "Add participant to conference" : "Merge into conference"}
                >
                  <PlusIcon width={24} height={24} />
                </button>
                <button
                  className={styles.controlBtn}
                  onClick={() => setTransferOpen(true)}
                  aria-label="Transfer call"
                >
                  <PhoneTransferIcon width={22} height={22} />
                </button>
                <button className={`${styles.controlBtn} ${styles.hangup}`} onClick={onHangup} aria-label="Hang up">
                  <PhoneHangupIcon width={24} height={24} />
                </button>
              </div>
            )}
            {isIncoming && (
              <div className={styles.incoming}>
                <button
                  className={styles.incomingAction}
                  onClick={() => {
                    setMinimized(false);
                    reject();
                  }}
                >
                  <span className={`${styles.incomingBtn} ${styles.declineBtn}`}>
                    <PhoneHangupIcon width={30} height={30} />
                  </span>
                  Decline
                </button>
                <button className={styles.incomingAction} onClick={answer}>
                  <span className={`${styles.incomingBtn} ${styles.answerBtn}`}>
                    <PhoneIcon width={30} height={30} />
                  </span>
                  Answer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {minimized && (
        <button className={styles.pip} onClick={() => setMinimized(false)} aria-label="Restore call">
          <span className={styles.pipStatus}>{isIncoming ? "Incoming" : formatDuration(seconds)}</span>
          <span className={styles.pipParty}>{party}</span>
          <PhoneHangupIcon width={18} height={18} />
        </button>
      )}
      <Sheet
        open={mergeOpen}
        onClose={() => { setMergeOpen(false); setMergeError(null); }}
        title={merged ? "Add participant" : "Merge call"}
        footer={
          <Button
            fullWidth
            isLoading={mergeBusy}
            disabled={!mergeTo.trim() || mergeBusy}
            onClick={() => void submitMerge()}
          >
            {merged ? "Add to conference" : "Merge"}
          </Button>
        }
      >
        <TextField
          label={merged ? "Add participant" : "Merge with"}
          type="tel"
          value={mergeTo}
          onChange={(e) => setMergeTo(e.target.value)}
          placeholder="+1…"
        />
        <p className={styles.mergeHint}>
          {merged
            ? "Dials another person into this conference."
            : "Everyone stays on the line — you, the current caller, and the person you add join a conference together."}
        </p>
        {mergeError && <p className={styles.transferError}>{mergeError}</p>}
      </Sheet>
      <Sheet
        open={transferOpen}
        onClose={closeTransfer}
        title="Transfer call"
        footer={
          <Button
            fullWidth
            isLoading={transferBusy}
            disabled={!transferTo.trim() || transferBusy}
            onClick={() => void submitTransfer()}
          >
            Transfer
          </Button>
        }
      >
        <TextField
          label="Transfer to"
          type="tel"
          value={transferTo}
          onChange={(e) => setTransferTo(e.target.value)}
          placeholder="+1…"
        />
        {transferError && <p className={styles.transferError}>{transferError}</p>}
      </Sheet>
    </>
  );

  return createPortal(overlay, document.body);
}